import cx from 'classnames';
import _debounce from 'lodash/debounce';
import {
  ComponentPropsWithoutRef,
  forwardRef,
  RefObject,
  useEffect,
  useState,
} from 'react';
import { Icon } from 'src/components-v2/DataDisplay';
import { Box } from 'src/components-v2/Layout';
import PlaidLogo from 'src/components-v2/SVGs/PlaidLogo';
import { Link } from 'src/components/Inputs';
import BaseIcon from 'threads5/Icon';
import styled from 'threads5/styles/styled';
import {
  CardWIcon,
  CardWImage,
  CustomerStoriesColumnItem,
  isCardWIcon,
  isCardWImage,
  isCustomerStoriesColumnItem,
  isLink,
  isRoundedButtonLink,
  Link as LinkType,
  NavDataItems,
  RoundedButtonLink,
  SubMenuData,
} from '../types';
import { Image } from 'src/components-v2/Media';
import styles from './MobileNav.module.scss';

type SectionLabel = {
  isSectionLabelMarker: true;
  title: string;
};

type ArrowLink = LinkType & { hasArrow: true };

type NavStackItem = {
  title: string;
  items: Array<
    | SubMenuData['children'][number]
    | SectionLabel
    | CustomerStoriesColumnItem
    | ArrowLink
  >;
};

const StyledUl = styled('ul')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  margin: '16px 0 32px',
  padding: 0,
});

const StyledLi = styled('li')({
  listStyle: 'none',
  padding: '0',
});

const StyledNavItemLayout = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '24px',
});

const StyledNavButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '24px',
  textAlign: 'left',
  width: '100%',
  padding: '15px 12px',
});

const StyledIconContainer = styled('span')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '8px',
  background:
    'linear-gradient(47.75deg, #f5f5ff 0%, #f7faff 50%, #e5fcff 100%)',
  border: '1px solid #e5effe',
  color: '#043c67',
  flexShrink: 0,
  '& svg': {
    color: 'inherit',
  },
  '& path': {
    fill: 'currentColor',
  },
});

const StyledBackButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '16px',
  fontWeight: '600',
  padding: '12px 0',
  paddingRight: '8px',
  color: '#111',
});

const StyledDivider = styled('hr')({
  border: 'none',
  backgroundColor: '#EAEFF0',
  height: '1px',
  margin: '12px 0',
});

const StyledSectionLabel = styled('div')({
  fontSize: '14px',
  fontWeight: '600',
  color: '#6B7280',
  padding: '0',
});

const StyledRoundedButtonLine = styled('div')({
  display: 'flex',
  marginTop: '0.8rem',
  a: {
    display: 'inline-flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '12px 24px',
    border: '1px solid #dde3e9',
    borderRadius: '999px',
    color: '#232424',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
    background: '#fff',
  },
});

const StyledImageCard = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#ffffff',
  border: '1px solid #ebf0f4',
  borderRadius: '12px',
  padding: '4px',
  overflow: 'hidden',
  marginTop: '8px',
});

const StyledImageCardImage = styled('div')({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  borderRadius: '8px',
  overflow: 'hidden',
  background:
    'linear-gradient(47.75deg, #f5f5ff 0%, #f7faff 50%, #e5fcff 100%)',
});

const StyledImageCardTitle = styled('div')({
  padding: '12px',
  fontSize: '16px',
  fontWeight: '600',
  color: '#111',
});

const ProductShot: Record<string, React.ReactNode> = {
  linkDemo: (
    <Image
      src='https://images.ctfassets.net/ss5kfr270og3/12v1ZRQBMolDRjOtObvzKP/16cea9928bb6abf118570358c053bc94/link-demo.png?fm=webp&q=70'
      width={550}
      height={408}
      alt=''
      sx={{ width: '100%', height: 'auto', display: 'block' }}
    />
  ),
  featured: (
    <Image
      src='https://images.ctfassets.net/ss5kfr270og3/3WFUS2lwcJUz0eDtH72SJS/058afcf102e63e128e6a0dadef476d8c/effects-26-nav-cta-on-demand.png?fm=webp&q=70'
      width={236}
      height={165}
      alt='Effects 26'
      sx={{ width: '100%', height: 'auto', display: 'block' }}
    />
  ),
  link: (
    <Image
      src='https://images.ctfassets.net/ss5kfr270og3/5rJPK58W3Dz1FWsA2vOT1e/1becbed98efe1e1413607110b17596fc/Link.png?fm=webp&q=70&w=326'
      width={632}
      height={884}
      alt=''
      sx={{ width: '100%', height: 'auto', display: 'block' }}
    />
  ),
  layer: (
    <Image
      src='https://images.ctfassets.net/ss5kfr270og3/0Akzu34Dw2ub564y1wrfA/90ed336cce5f19026a31408458d242c0/Layer.png?fm=webp&q=70&w=326'
      width={632}
      height={884}
      alt=''
      sx={{ width: '100%', height: 'auto', display: 'block' }}
    />
  ),
};

const NavImageCard = ({
  item,
  onClick,
}: {
  item: CardWImage;
  onClick?: () => void;
}) => {
  const { title, href, trackingId, locale, image } = item;

  return (
    <Link href={href} trackingId={trackingId} locale={locale}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- error inaccurate, href added by Link */}
      <a onClick={onClick}>
        <StyledImageCard>
          <StyledImageCardImage>
            {ProductShot[image] || null}
          </StyledImageCardImage>
          <StyledImageCardTitle>{title}</StyledImageCardTitle>
        </StyledImageCard>
      </a>
    </Link>
  );
};

const NavIconCard = ({
  title,
  href,
  trackingId,
  locale,
  icon,
  description,
  onClick,
}: {
  title: string;
  href: string;
  trackingId?: string;
  locale?: string;
  icon: CardWIcon['icon'];
  description?: string;
  onClick?: () => void;
}) => {
  return (
    <Link href={href} trackingId={trackingId} locale={locale}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- error inaccurate, href added by Link */}
      <a onClick={onClick}>
        <StyledNavItemLayout
          sx={{
            color: 'base/black/1000',
            padding: '8px 0',
          }}
        >
          <StyledIconContainer>
            <Icon icon={icon} size={20} />
          </StyledIconContainer>
          <Box
            component='span'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              lineHeight: '28px',
            }}
          >
            {title}
            {description ? (
              <Box
                component='span'
                sx={{
                  color: 'base/minted/900',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                {description}
              </Box>
            ) : null}
          </Box>
        </StyledNavItemLayout>
      </a>
    </Link>
  );
};

const customerStoryImages: Record<
  string,
  { src: string; width: number; height: number; alt: string }
> = {
  affirm: {
    src: 'https://images.ctfassets.net/ss5kfr270og3/74lu4wEJLRNTiZgtv8kf0N/c8ed6399cdaa38be54d78e31a9adb570/affirm-logo.png',
    width: 38.68,
    height: 15.43,
    alt: '',
  },
  robinhood: {
    src: 'https://images.ctfassets.net/ss5kfr270og3/4syXDPD9rqQQbMfXB75rBo/5a0ff8d09baaba115eb90783bbf43bac/robinhood-logo.png',
    width: 59.2,
    height: 11.61,
    alt: '',
  },
  chime: {
    src: 'https://images.ctfassets.net/ss5kfr270og3/1nz7suAOHxLcuGrXWIpTin/690fc3b32d026dfb94941705815d77c0/chime-logo.png',
    width: 39,
    height: 11.85,
    alt: '',
  },
};

const StyledStoriesSection = styled('div')({
  position: 'relative',
  marginTop: '16px',
  marginLeft: '-16px',
  marginRight: '-16px',
  marginBottom: '-32px',
  padding: '16px',
  paddingTop: '20px',
  paddingBottom: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  backgroundColor: '#F7FAFF',
  backgroundImage:
    'url(https://images.ctfassets.net/ss5kfr270og3/2iEQDrZaoMIc0fvMD8Jaby/fce48cda02962dc9730b9349a9be3f81/thumbprint-bg.png?q=70&fm=webp)',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'top right',
  backgroundSize: '280px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background:
      'linear-gradient(90deg, #9abaf9 0%, #c6b2f7 10%, #f1c5e7 20%, #e9d6e6 30%, #c3e8e8 40%, #97e0f1 50%, #becaf5 60%, #ebbded 70%, #edbcec 80%, #efbbeb 90%, #ddbcf1 100%)',
  },
});

const StyledStoriesList = styled('ul')({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const StyledStoryItem = styled('li')(({ theme }) => {
  return {
    width: '100%',
    a: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      color: 'inherit',
      textDecoration: 'none',
    },
  };
});

const StyledStoryImageWrapper = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '88px',
  height: '61px',
  borderRadius: '6px',
  background:
    'linear-gradient(47.75deg, #e6e6ff 0%, #e5effe 50%, #ccf8ff 100%)',
  flexShrink: 0,
});

const StyledStoryTitle = styled('span')({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: '600',
  color: '#232424',
});

const StyledSeeAllLine = styled('div')({
  marginTop: '16px',
  display: 'flex',
  a: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#232424',
    textDecoration: 'none',
    gap: '4px',
  },
});

const CustomerStoriesSection = ({
  item,
  onClick,
}: {
  item: CustomerStoriesColumnItem;
  onClick?: () => void;
}) => {
  return (
    <StyledStoriesSection>
      {item.title && <StyledSectionLabel>{item.title}</StyledSectionLabel>}
      <StyledStoriesList>
        {item.stories.map((story, i) => {
          const imageData = customerStoryImages[story.image];
          return (
            <StyledStoryItem key={`customer-story-${i}`}>
              <Link href={story.href}>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- error inaccurate, href added by Link */}
                <a onClick={onClick}>
                  <StyledStoryImageWrapper>
                    {imageData && (
                      <img
                        src={imageData.src}
                        width={imageData.width}
                        height={imageData.height}
                        alt={imageData.alt}
                      />
                    )}
                  </StyledStoryImageWrapper>
                  <StyledStoryTitle>{story.title}</StyledStoryTitle>
                </a>
              </Link>
            </StyledStoryItem>
          );
        })}
      </StyledStoriesList>
      <StyledSeeAllLine>
        <Link href={item.seeAllLink.href}>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- error inaccurate, href added by Link */}
          <a onClick={onClick}>
            {item.seeAllLink.title}
            <BaseIcon name='ArrowRight' size={14} />
          </a>
        </Link>
      </StyledSeeAllLine>
    </StyledStoriesSection>
  );
};

const NavItem = ({
  item,
  onClick,
}: {
  item: LinkType | CardWIcon | CardWImage | RoundedButtonLink | ArrowLink;
  onClick?: () => void;
}) => {
  // rounded button links
  if (isRoundedButtonLink(item)) {
    return (
      <StyledRoundedButtonLine>
        <Link
          href={item.href}
          trackingId={item.trackingId}
          locale={item.locale}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- error inaccurate, href added by Link */}
          <a onClick={onClick}>
            {item.title}
            <BaseIcon
              name='ArrowRight'
              size={14}
              sx={{ width: 10, height: 10 }}
            />
          </a>
        </Link>
      </StyledRoundedButtonLine>
    );
  }

  // image cards (or as icon card when mobile override is set)
  if (isCardWImage(item)) {
    if (item.mobileOverride?.icon) {
      return (
        <NavIconCard
          title={item.title}
          href={item.href}
          trackingId={item.trackingId}
          locale={item.locale}
          icon={item.mobileOverride.icon}
          description={item.description}
          onClick={onClick}
        />
      );
    }
    return <NavImageCard item={item} onClick={onClick} />;
  }

  const { title, href, trackingId, locale, ...rest } = item;
  const hasIcon = isCardWIcon(rest);

  if (hasIcon) {
    return (
      <NavIconCard
        title={title}
        href={href}
        trackingId={trackingId}
        locale={locale}
        icon={rest.icon}
        description={rest.description}
        onClick={onClick}
      />
    );
  }

  return (
    <Link href={href} trackingId={trackingId} locale={locale}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- error inaccurate, href added by Link */}
      <a onClick={onClick}>
        <StyledNavItemLayout
          sx={{
            color: 'base/black/1000',
            padding: '15px 12px',
            ...('hasArrow' in rest && {
              justifyContent: 'space-between',
              gap: '8px',
            }),
          }}
        >
          <Box
            component='span'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              lineHeight: '28px',
            }}
          >
            {title}
          </Box>
          {'hasArrow' in rest && <BaseIcon name='ArrowRight' size={16} />}
        </StyledNavItemLayout>
      </a>
    </Link>
  );
};

const isSectionLabel = (item: unknown): item is SectionLabel => {
  return (
    typeof item === 'object' &&
    item !== null &&
    'isSectionLabelMarker' in item &&
    (item as SectionLabel).isSectionLabelMarker === true
  );
};

// flatten nested children and add section labels
const flattenChildrenWithLabels = (
  children: SubMenuData['children'],
): Array<
  SubMenuData['children'][number] | SectionLabel | CustomerStoriesColumnItem
> => {
  if (!children || children.length === 0) return children;

  const firstChild = children[0];
  const isContainerItem =
    firstChild &&
    typeof firstChild === 'object' &&
    ('children' in firstChild || 'stories' in firstChild) &&
    !('href' in firstChild) &&
    !('type' in firstChild);

  if (isContainerItem) {
    const result: Array<
      SubMenuData['children'][number] | SectionLabel | CustomerStoriesColumnItem
    > = [];
    (
      children as Array<{
        title?: string;
        children?: unknown[];
        stories?: unknown[];
        image?: unknown;
        mobileOverride?: CustomerStoriesColumnItem;
      }>
    ).forEach((child) => {
      // add CustomerStoriesColumnItem directly
      if ('stories' in child && isCustomerStoriesColumnItem(child)) {
        result.push(child);
      } else if (
        'image' in child &&
        child.mobileOverride &&
        isCustomerStoriesColumnItem(child.mobileOverride)
      ) {
        // optional override from image card to customer stories
        result.push(child.mobileOverride);
      } else if ('children' in child) {
        if (child.title) {
          result.push({ isSectionLabelMarker: true, title: child.title });
        }
        if (child.children) {
          result.push(...(child.children as SubMenuData['children']));
        }
      }
    });
    return result;
  }

  return children;
};

const NavPageItem = ({
  item,
  onNavigate,
  onLinkClick,
}: {
  item: SubMenuData | (LinkType & { id?: string }) | CardWIcon | ArrowLink;
  onNavigate: (stackItem: NavStackItem) => void;
  onLinkClick?: () => void;
}) => {
  // nav item
  if (isLink(item)) {
    return <NavItem item={item} onClick={onLinkClick} />;
  }

  // sub menu link
  const subMenuData = item as SubMenuData;

  const handleClick = () => {
    if (subMenuData.children && subMenuData.children.length > 0) {
      const itemsToShow = flattenChildrenWithLabels(subMenuData.children);
      const tabListLink =
        'tabListLink' in subMenuData ? subMenuData.tabListLink : undefined;
      onNavigate({
        title: subMenuData.title,
        items: tabListLink
          ? [...itemsToShow, { ...tabListLink, hasArrow: true }]
          : itemsToShow,
      });
    }
  };

  return (
    <StyledNavButton
      onClick={handleClick}
      sx={{
        color: 'base/black/1000',
      }}
    >
      {subMenuData.title}
      <BaseIcon name='ArrowRight' size={16} />
    </StyledNavButton>
  );
};

type MobileNavProps = ComponentPropsWithoutRef<'dialog'> & {
  className?: string;
  items: NavDataItems;
  trackingIds: { getAPIkeys: string };
  onClose?: () => void;
};

export const MobileNav = forwardRef(
  (props: MobileNavProps, ref: RefObject<HTMLDialogElement>) => {
    const { className, items, trackingIds, onClose } = props;
    const [navStack, setNavStack] = useState<NavStackItem[]>([]);

    const isAtRoot = navStack.length === 0;
    const currentPage = navStack[navStack.length - 1];
    const currentItems = isAtRoot ? items : currentPage?.items ?? [];

    const handleClose = () => {
      setNavStack([]);
      onClose?.();
    };

    const handleNavigate = (stackItem: NavStackItem) => {
      setNavStack((prev) => {
        return [...prev, stackItem];
      });
    };

    const handleBack = () => {
      setNavStack((prev) => {
        return prev.slice(0, -1);
      });
    };

    useEffect(() => {
      if (typeof window === 'undefined') return;

      const handleResize = _debounce(() => {
        if (ref?.current?.open) {
          if (window.innerWidth > 1077) {
            setNavStack([]);
            onClose?.();
          }
        }
      }, 100);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [ref, onClose]);

    return (
      <dialog
        className={cx(styles.Dialog, className)}
        id='main-menu-dialog'
        ref={ref}
      >
        <div className={styles.Dialog__inner}>
          <div className={styles.Dialog__head}>
            <form method='dialog'>
              <button
                type='button'
                className={styles.Close}
                tabIndex={0}
                aria-label='Close main menu modal'
                onClick={handleClose}
              >
                <Icon icon='Close' sx={{ width: 18, height: 20 }} />
              </button>
              <input
                type='submit'
                value='Close main menu modal'
                style={{ display: 'none' }}
              />
            </form>
            {isAtRoot ? (
              <Link href='/'>
                <a className={styles.Logo} aria-label='Go to Plaid homepage'>
                  <PlaidLogo
                    sx={{
                      display: 'block',
                      height: 28,
                      boxSizing: 'border-box',
                      marginLeft: '16px',
                      color: '#111',
                    }}
                  />
                </a>
              </Link>
            ) : (
              <StyledBackButton
                type='button'
                onClick={handleBack}
                aria-label='Go back'
              >
                <BaseIcon name='ArrowLeft' size={16} />
                Back
              </StyledBackButton>
            )}
          </div>

          <div className={styles.Dialog__body}>
            <Box
              key={navStack.length}
              sx={{
                flexGrow: 2,
              }}
            >
              <StyledUl>
                {currentItems.map((item, i) => {
                  if (isSectionLabel(item)) {
                    const isFirstSection = i === 0;
                    return (
                      <StyledLi key={`section-${item.title}-${i}`}>
                        <StyledSectionLabel
                          sx={{ paddingTop: isFirstSection ? '4px' : '16px' }}
                        >
                          {item.title}
                        </StyledSectionLabel>
                      </StyledLi>
                    );
                  }
                  if (isCustomerStoriesColumnItem(item)) {
                    return (
                      <StyledLi key={`customer-stories-${i}`}>
                        <CustomerStoriesSection
                          item={item}
                          onClick={handleClose}
                        />
                      </StyledLi>
                    );
                  }
                  const itemId = 'id' in item ? item.id : null;
                  const itemHref = 'href' in item ? item.href : null;
                  const itemTitle = 'title' in item ? item.title : null;
                  const key = itemId ?? `${itemHref}-${itemTitle}-${i}`;
                  return (
                    <StyledLi key={key}>
                      <NavPageItem
                        item={item}
                        onNavigate={handleNavigate}
                        onLinkClick={handleClose}
                      />
                    </StyledLi>
                  );
                })}
                {isAtRoot && (
                  <>
                    <StyledDivider />
                    <StyledLi>
                      <NavItem
                        item={{
                          title: 'Log in',
                          href: 'https://dashboard.plaid.com/',
                        }}
                        onClick={handleClose}
                      />
                    </StyledLi>
                    <StyledLi>
                      <NavItem
                        item={{
                          title: 'Contact sales',
                          href: '/contact/',
                          trackingId: trackingIds?.getAPIkeys,
                        }}
                        onClick={handleClose}
                      />
                    </StyledLi>
                  </>
                )}
              </StyledUl>
            </Box>
          </div>
        </div>
      </dialog>
    );
  },
);
